import React from 'react';
import { Tag, Avatar, Tooltip } from 'antd';
import { CalendarOutlined, PushpinFilled, ProjectOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toJalali } from '../../../utils/dateUtils';
import { ActivityFaIcon } from '../../common/FollowUpActivitySelect';

const priorityMeta = {
  high: { color: 'red', text: 'بالا' },
  medium: { color: 'blue', text: 'متوسط' },
  low: { color: 'default', text: 'پایین' },
};

const formatCardDate = (value) => {
  if (!value) return null;
  return toJalali(value) || value;
};

const isVisibleOnBoard = (field) => {
  const v = field?.visible_in_board;
  return v === 1 || v === true || v === '1' || v === 'yes';
};

const getBoardCustomFields = (task) => {
  const group = task?.custom_fields;
  const raw = Array.isArray(group)
    ? group
    : group?.fields || group?.custom_field || [];
  if (!Array.isArray(raw)) return [];
  return raw.filter((f) => isVisibleOnBoard(f) && f.value != null && String(f.value).trim() !== '');
};

const formatCustomFieldValue = (field) => {
  const raw = field?.value;
  if (raw == null || raw === '') return '—';
  if (field?.type === 'number' || /^-?\d+(\.\d+)?$/.test(String(raw).trim())) {
    const num = Number(raw);
    if (Number.isFinite(num)) {
      return new Intl.NumberFormat('fa-IR').format(num);
    }
  }
  return String(raw);
};

const TaskCustomFields = ({ fields }) => {
  if (!fields?.length) return null;

  return (
    <div className="mt-2 space-y-1 task-kanban-custom-fields">
      {fields.map((field) => {
        const title = field.name || field.name || '';
        const display = formatCustomFieldValue(field);
        return (
          <Tooltip key={field.id || field.name} title={`${title}: ${display}`}>
            <div className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300 min-w-0">
              <ActivityFaIcon
                icon={field.icon || field.name}
                color="#64748b"
                size={12}
                className="shrink-0"
              />
              <span className="truncate" style={{ fontFamily: 'CustomFont' }}>
                <span className="text-gray-400 dark:text-gray-500 ml-0.5">{title}:</span>
                {display}
              </span>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export const TaskCardContent = ({ task, onClick, dragHandleProps = {} }) => {
  const users = task.users || [];
  const labels = task.labels || task.task_labels || [];
  const pm = priorityMeta[task.priority] || { color: 'default', text: task.priority };
  // due_date (ISO) را ترجیح بده؛ due_on فرمت مبهم d-m-Y/m-d-Y دارد و اشتباه تبدیل می‌شود
  const dueJalali = formatCardDate(task.due_date || task.due_on);
  const boardFields = getBoardCustomFields(task);

  return (
    <div
      onClick={() => onClick?.(task)}
      role="presentation"
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md cursor-grab active:cursor-grabbing transition-shadow"
      style={{ fontFamily: 'CustomFont' }}
      {...dragHandleProps}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 m-0 line-clamp-2 leading-snug flex-1">
          {task.heading}
        </h4>
        {task.pinned_task || task.pinned ? <PushpinFilled className="text-amber-500 text-xs shrink-0 mt-0.5" /> : null}
      </div>
      {task.project_name ? (
        <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
          <ProjectOutlined />
          <span className="truncate">{task.project_name}</span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1 mt-2">
        <Tag color={pm.color} className="m-0 text-[10px]">
          {pm.text}
        </Tag>
        {labels.slice(0, 2).map((l) => (
          <Tag key={l.id} color={l.label_color || l.color} className="m-0 text-[10px]">
            {l.label_name || l.name}
          </Tag>
        ))}
      </div>

      <TaskCustomFields fields={boardFields} />

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <CalendarOutlined />
          <span>{dueJalali ? `سررسید: ${dueJalali}` : 'بدون سررسید'}</span>
        </div>
        <Avatar.Group maxCount={3} size="small">
          {users.map((u) => (
            <Tooltip key={u.id} title={u.name}>
              <Avatar src={u.image_url || u.image} size={22}>
                {u.name?.[0]}
              </Avatar>
            </Tooltip>
          ))}
        </Avatar.Group>
      </div>
    </div>
  );
};

const SortableTaskCard = ({ task, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task-${task.id}`,
    data: { type: 'task', task },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <TaskCardContent task={task} onClick={onClick} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
};

export const TaskCardOverlay = ({ task }) => <TaskCardContent task={task} />;

const TaskCard = ({ task, onClick, overlay = false }) => {
  if (overlay) {
    return <TaskCardOverlay task={task} />;
  }
  return <SortableTaskCard task={task} onClick={onClick} />;
};

export default TaskCard;
